import type {
  CalculationState,
  AvailableCalculationScenarios,
  CalculationScenarioAvailability,
  CompletedCalculationTactic
} from '../../index';
import { getLoadFlowAvailability } from './getLoadFlowAvailability';
import { getShortCircuitAvailability } from './getShortCircuitAvailability';

const handleWhenGridElementSelected = (
  selectedGridElement: Extract<
    CalculationState['selectedGridElement'],
    { type: 'SELECTED_GRID_ELEMENT' }
  >
): AvailableCalculationScenarios => {
  if (
    (selectedGridElement.gridElement.loadflow.type === 'LOAD_FLOW_DETAILS' ||
      selectedGridElement.gridElement.loadflow.type ===
        'ALL_LOAD_FLOW_SCENARIO_DETAILS') &&
    selectedGridElement.gridElement.shortCircuit.type ===
      'SHORT_CIRCUIT_DETAILS'
  )
    return { type: 'ALL_AVAILABLE' };
  if (
    selectedGridElement.gridElement.loadflow.type === 'LOAD_FLOW_DETAILS' ||
    selectedGridElement.gridElement.loadflow.type ===
      'ALL_LOAD_FLOW_SCENARIO_DETAILS'
  )
    return { type: 'SOME_AVAILABLE', scenarios: new Set(['LOAD_FLOW']) };
  if (
    selectedGridElement.gridElement.shortCircuit.type ===
    'SHORT_CIRCUIT_DETAILS'
  )
    return { type: 'SOME_AVAILABLE', scenarios: new Set(['SHORT_CIRCUIT']) };

  return { type: 'NONE_AVAILABLE' };
};

export const availableCalculationScenarios = (
  state: CalculationState
): AvailableCalculationScenarios => {
  if (state.selectedGridElement.type === 'SELECTED_GRID_ELEMENT') {
    return handleWhenGridElementSelected(state.selectedGridElement);
  }

  if (state.calculationTactic.current !== 'LOADED')
    return { type: 'NONE_AVAILABLE' };
  if (
    state.calculationTactic.current === 'LOADED' &&
    state.calculationTactic.value.status === 'Failed'
  )
    return { type: 'NONE_AVAILABLE' };
  if (state.calculationTactic.value.status === 'Completed') {
    return handleWhenCompletedCalculationSet(state.calculationTactic.value);
  }

  return { type: 'NONE_AVAILABLE' };
};

export function findAllAvailableCalculationScenarios(
  ...calculationScenarioAvailabilities: CalculationScenarioAvailability<
    'LOAD_FLOW' | 'SHORT_CIRCUIT'
  >[]
) {
  return calculationScenarioAvailabilities
    .filter(availability => availability.type === 'AVAILABLE')
    .map(
      availability =>
        (availability as Extract<typeof availability, { type: 'AVAILABLE' }>)
          .calculationType
    );
}
function handleWhenCompletedCalculationSet(
  calculationTactic: CompletedCalculationTactic
): AvailableCalculationScenarios {
  const loadFlowAvailability = getLoadFlowAvailability(
    calculationTactic.calculationSetResult.calculationScenarios.loadFlow
  );
  const shortCircuitAvailability = getShortCircuitAvailability(
    calculationTactic.calculationSetResult.calculationScenarios.shortCircuit
  );

  if (
    shortCircuitAvailability.type === 'AVAILABLE' &&
    loadFlowAvailability.type === 'AVAILABLE'
  )
    return { type: 'ALL_AVAILABLE' };

  const calculations = findAllAvailableCalculationScenarios(
    shortCircuitAvailability,
    loadFlowAvailability
  );

  if (calculations.length === 0) return { type: 'NONE_AVAILABLE' };

  return { type: 'SOME_AVAILABLE', scenarios: new Set(calculations) };
}
